import React, { useEffect, useState } from "react";
import axios from "axios";
import ApiEndpoints from "components/Api";
import useLoading from "components/Loading";
import useMessageBox from "components/MessageBox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import "./UserManagement.css";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [initialUserGroupIds, setInitialUserGroupIds] = useState([]);
  const [selectedUserGroupIds, setSelectedUserGroupIds] = useState([]);
  const [newUser, setNewUser] = useState({
    login: "",
    password: "",
    firstName: "",
    lastName: "",
    email: "",
  });
  const [newPassword, setNewPassword] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "ascending" });

  const [showLoading, hideLoading, Loading] = useLoading();
  const [showMessage, MessageBox] = useMessageBox();

  useEffect(() => {
    fetchUsers();
  }, []);

  const authHeader = () => {
    const token = localStorage.getItem("authToken");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUsers = async () => {
    try {
      showLoading();
      const response = await axios.get(ApiEndpoints.ADMIN_USERS, {
        headers: authHeader(),
      });
      setUsers(response.data);
      hideLoading();
    } catch (e) {
      hideLoading();
    }
  };

  const fetchGroups = async () => {
    const response = await axios.get(ApiEndpoints.ADMIN_GROUPS, {
      headers: authHeader(),
    });
    setGroups(response.data);
  };

  const openAddModal = () => {
    setNewUser({ login: "", password: "", firstName: "", lastName: "", email: "" });
    setShowAddModal(true);
  };

  const addUser = async () => {
    try {
      await axios.post(ApiEndpoints.ADMIN_USERS_ADD, newUser, {
        headers: authHeader(),
      });
      setShowAddModal(false);
      showMessage("Dodano użytkownika", "success");
      fetchUsers();
    } catch (e) {
      showMessage("Błąd dodawania", "error");
    }
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowPasswordModal(true);
  };

  const resetPassword = async () => {
    if (!selectedUser) return;
    try {
      await axios.put(
        ApiEndpoints.ADMIN_RESET_PASSWORD(selectedUser.id),
        { newPassword },
        { headers: authHeader() }
      );
      setShowPasswordModal(false);
      showMessage("Zmieniono hasło", "success");
    } catch (e) {
      showMessage("Błąd zmiany hasła", "error");
    }
  };

  const openGroupModal = async (user) => {
    setSelectedUser(user);
    const userGroupIds = user.groups ? user.groups.map((g) => g.id) : [];
    setInitialUserGroupIds(userGroupIds);
    setSelectedUserGroupIds(userGroupIds);
    await fetchGroups();
    setShowGroupModal(true);
  };

  const toggleGroup = (groupId) => {
    setSelectedUserGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const saveGroups = async () => {
    if (!selectedUser) return;
    try {
      for (const id of selectedUserGroupIds) {
        if (!initialUserGroupIds.includes(id)) {
          await axios.post(
            ApiEndpoints.ADMIN_ADD_USER_TO_GROUP(selectedUser.id, id),
            {},
            { headers: authHeader() }
          );
        }
      }
      for (const id of initialUserGroupIds) {
        if (!selectedUserGroupIds.includes(id)) {
          await axios.delete(
            ApiEndpoints.ADMIN_REMOVE_USER_FROM_GROUP(selectedUser.id, id),
            { headers: authHeader() }
          );
        }
      }
      setShowGroupModal(false);
      fetchUsers();
      showMessage("Zmieniono grupy", "success");
    } catch (e) {
      showMessage("Błąd zmiany grupy", "error");
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Usunąć użytkownika?")) return;
    try {
      showLoading();
      await axios.delete(ApiEndpoints.ADMIN_DELETE_USER(id), { headers: authHeader() });
      hideLoading();
      showMessage("Usunięto użytkownika", "success");
      fetchUsers();
    } catch (e) {
      hideLoading();
      showMessage("Błąd usuwania", "error");
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "ascending" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "ascending" ? 1 : -1;
    return 0;
  });

  return (
    <div className="user-management-container">
      <Loading />
      <MessageBox />
      <Table className="user-table">
        <TableHead>
          <TableRow>
            <TableCell onClick={() => requestSort("id")}>
              ID {sortConfig.key === "id" && (
                <Icon>{sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}</Icon>
              )}
            </TableCell>
            <TableCell onClick={() => requestSort("login")}> 
              Login {sortConfig.key === "login" && (
                <Icon>{sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}</Icon>
              )}
            </TableCell>
            <TableCell onClick={() => requestSort("firstName")}> 
              Imię {sortConfig.key === "firstName" && (
                <Icon>{sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}</Icon>
              )}
            </TableCell>
            <TableCell onClick={() => requestSort("lastName")}> 
              Nazwisko {sortConfig.key === "lastName" && (
                <Icon>{sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}</Icon>
              )}
            </TableCell>
            <TableCell>Grupy</TableCell>
            <TableCell>Akcje</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.id}</TableCell>
              <TableCell>{user.login}</TableCell>
              <TableCell>{user.firstName}</TableCell>
              <TableCell>{user.lastName}</TableCell>
              <TableCell className="groups-column">{user.groups ? user.groups.map((g) => g.name).join(', ') : ''}</TableCell>
              <TableCell className="action-buttons">
                <div className="action-buttons-container">
                  <Button color="success" variant="contained" size="small" onClick={() => openPasswordModal(user)}>Hasło</Button>
                  <Button color="success" variant="contained" size="small" onClick={() => openGroupModal(user)}>Grupy</Button>
                  <Button color="success" variant="contained" size="small" onClick={() => deleteUser(user.id)}>Usuń</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="user-management-header">
        <Button color="success" variant="contained" onClick={openAddModal}>
          Dodaj użytkownika
        </Button>
      </div>

      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)}>
        <DialogTitle>Dodaj użytkownika</DialogTitle>
        <DialogContent>
          <TextField
            label="Login"
            fullWidth
            margin="dense"
            value={newUser.login}
            onChange={(e) => setNewUser({ ...newUser, login: e.target.value })}
          />
          <TextField
            type="password"
            label="Hasło"
            fullWidth
            margin="dense"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
          />
          <TextField
            label="Imię"
            fullWidth
            margin="dense"
            value={newUser.firstName}
            onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
          />
          <TextField
            label="Nazwisko"
            fullWidth
            margin="dense"
            value={newUser.lastName}
            onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            margin="dense"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button color="success" onClick={addUser} variant="contained">Zapisz</Button>
          <Button onClick={() => setShowAddModal(false)} variant="outlined">Anuluj</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showPasswordModal && !!selectedUser} onClose={() => setShowPasswordModal(false)}>
        <DialogTitle>Zmień hasło dla {selectedUser?.login}</DialogTitle>
        <DialogContent>
          <TextField
            type="password"
            label="Nowe hasło"
            fullWidth
            margin="dense"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button color="success" onClick={resetPassword} variant="contained">Zapisz</Button>
          <Button onClick={() => setShowPasswordModal(false)} variant="outlined">Anuluj</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showGroupModal && !!selectedUser} onClose={() => setShowGroupModal(false)}>
        <DialogTitle>Grupy użytkownika {selectedUser?.login}</DialogTitle>
        <DialogContent>
          <FormGroup sx={{ alignItems: "flex-start" }}>
            {groups.map((g) => (
              <FormControlLabel
                key={g.id}
                control={
                  <Checkbox
                    checked={selectedUserGroupIds.includes(g.id)}
                    onChange={() => toggleGroup(g.id)}
                  />
                }
                label={g.name}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button color="success" onClick={saveGroups} variant="contained">Zapisz</Button>
          <Button onClick={() => setShowGroupModal(false)} variant="outlined">Anuluj</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserManagement;
